/**
 * FeaturedShopsCarousel.jsx
 *
 * Wide-rectangle carousel for "golden badge" premium shops.
 * Uses the same visual pattern as DiscountedProductsCarousel:
 *   - 16:10 image fills the whole card
 *   - Shop name + specialty + category badge overlay the image
 *   - Location shown BELOW the image
 *   - Auto-slide every 4.5s (paused on hover / touch)
 *   - Manual arrow buttons + dot indicators
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, MapPin, ChevronLeft, ChevronRight, Store, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { shopName, catName } from "@/i18n/dynamic";

const AUTOPLAY_MS = 4500;

export default function FeaturedShopsCarousel({ shops = [], cats = [] }) {
  const { lang } = useLang();
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);

  const total = shops.length;

  const scrollToIdx = useCallback((idx) => {
    const el = scrollerRef.current;
    if (!el || !el.firstChild) return;
    const cardW = el.firstChild.offsetWidth;
    const gap = 12;
    el.scrollTo({ left: idx * (cardW + gap) * (document.documentElement.dir === "rtl" ? -1 : 1), behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (paused || dragging || total <= 1) return;
    const timer = setInterval(() => {
      const nextIdx = (activeIdx + 1) % total;
      scrollToIdx(nextIdx);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [activeIdx, paused, dragging, total, scrollToIdx]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardW = el.firstChild?.offsetWidth || 1;
      const gap = 12;
      const idx = Math.round(el.scrollLeft / (cardW + gap));
      const absIdx = Math.min(total - 1, Math.max(0, Math.abs(idx)));
      setActiveIdx(absIdx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [total]);

  const next = () => scrollToIdx((activeIdx + 1) % total);
  const prev = () => scrollToIdx((activeIdx - 1 + total) % total);

  if (!total) return null;

  return (
    <div
      className="relative"
      data-testid="featured-shops-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setDragging(true)}
      onTouchEnd={() => setDragging(false)}
    >
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="السابق"
            data-testid="featured-arrow-prev"
            className="hidden md:flex absolute end-1 top-1/2 -translate-y-1/2 z-20 size-10 rounded-full bg-black/60 hover:bg-black/80 text-white items-center justify-center backdrop-blur-md border border-amber-400/40 shadow-lg transition"
          >
            <ChevronRight className="size-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="التالي"
            data-testid="featured-arrow-next"
            className="hidden md:flex absolute start-1 top-1/2 -translate-y-1/2 z-20 size-10 rounded-full bg-black/60 hover:bg-black/80 text-white items-center justify-center backdrop-blur-md border border-amber-400/40 shadow-lg transition"
          >
            <ChevronLeft className="size-5" />
          </button>
        </>
      )}

      <div
        ref={scrollerRef}
        className="kh-scroll-x flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scroll-smooth"
      >
        {shops.map((s) => {
          const shopCat = cats.find(c => c.slug === s.category_slug);
          const catLabel = shopCat ? catName(shopCat, lang) : s.category_slug || "";
          return (
            <Link
              to={`/shop/${s.id}`}
              key={s.id}
              state={{ shop: s }}
              onTouchStart={() => api.get(`/shops/${s.id}/bundle`).catch(() => {})}
              data-testid={`featured-shop-${s.id}`}
              className="card-lift shrink-0 snap-start rounded-2xl overflow-hidden relative bg-card transition group border-2"
              style={{
                width: "min(88vw, 340px)",
                borderColor: "rgba(245, 158, 11, 0.35)",
                boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.15), 0 6px 24px rgba(245, 158, 11, 0.2)",
              }}
            >
              {/* Full-bleed image (16:10 wide rectangle) */}
              <div className="relative aspect-[16/10] w-full overflow-hidden">
                {s.cover_image ? (
                  <img
                    src={s.cover_image}
                    alt={shopName(s, lang)}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-900/40 via-slate-900 to-black flex items-center justify-center">
                    <Store className="size-14 text-amber-400/30" />
                  </div>
                )}

                {/* Dark gradient scrim for text readability */}
                <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                {/* "Premium/Winner" pill (top-start) */}
                <div className="absolute top-2.5 start-2.5 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 text-slate-900 text-[11px] font-black shadow-lg">
                  {s.is_weekly_winner ? <Sparkles className="size-3 fill-slate-900" /> : <Crown className="size-3 fill-slate-900" />}
                  {s.is_weekly_winner ? "نجم الأسبوع" : "مميز"}
                </div>

                {/* Bookings pill for doctors/medical (top-end) */}
                {(s.category_slug === "doctors" || s.category_slug === "medical") && s.accepting_bookings && (
                  <div className="absolute top-2.5 end-2.5 z-10 px-2.5 py-1 rounded-full bg-emerald-500/95 text-white text-[10px] font-bold shadow-lg backdrop-blur-sm">
                    يقبل الحجز
                  </div>
                )}

                {/* Text overlaid on image (bottom) */}
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  {catLabel && (
                    <div className="text-[11px] font-bold text-amber-300 uppercase tracking-wider line-clamp-1 mb-0.5">
                      {catLabel}
                    </div>
                  )}
                  <h3 className="text-base font-black leading-tight line-clamp-1 drop-shadow">
                    {shopName(s, lang)}
                  </h3>
                  {s.specialty && (
                    <div className="text-[12px] font-semibold text-white/85 line-clamp-1 mt-0.5">
                      {s.specialty}
                    </div>
                  )}
                </div>
              </div>

              {/* Location bar BELOW the image */}
              {s.address && (
                <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground bg-muted/40 border-t">
                  <MapPin className="size-3.5 text-amber-500 shrink-0" />
                  <span className="truncate">{s.address}</span>
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Dot indicators */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {shops.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToIdx(i)}
              aria-label={`الانتقال إلى الشريحة ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIdx ? "w-6 bg-amber-500" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
